import { useState, useRef, useEffect } from 'react'
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Alert, Stack, Divider, LinearProgress,
} from '@mui/material'
import { Analytics, MergeType } from '@mui/icons-material'
import { Modal, Button } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const POLL_INTERVAL = 1200

/**
 * Find and merge duplicate assets. Duplicates are judged purely on content
 * (auto tag + database id are ignored); each group is collapsed into its oldest
 * member, summing the units. Analysis and merge both run as background tasks and
 * the dialog polls for progress.
 *  1. "Analyze" (dry run) -> preview the duplicate groups that would merge.
 *  2. Review, then "Merge Duplicates" to commit (irreversible).
 */
const AssetDedupeDialog = ({ open, onClose, onSuccess }) => {
  const [phase, setPhase] = useState(null) // 'analyzing' | 'merging' | null
  const [progress, setProgress] = useState(null) // {processed, total}
  const [preview, setPreview] = useState(null)
  const pollRef = useRef(null)

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  useEffect(() => () => stopPolling(), [])

  const reset = () => {
    stopPolling()
    setPhase(null)
    setProgress(null)
    setPreview(null)
  }

  const busy = phase !== null

  const handleClose = () => {
    if (busy) return
    reset()
    onClose()
  }

  const pollStatus = (taskId, dryRun) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get('/assets/assets/import_status/', { params: { task_id: taskId } })
        const { state, progress: prog, result, error } = res.data
        if (state === 'PROGRESS' && prog) {
          setProgress({ processed: prog.processed, total: prog.total })
        } else if (state === 'SUCCESS') {
          stopPolling()
          setPhase(null)
          setProgress(null)
          if (dryRun) {
            setPreview(result)
          } else {
            const t = result?.totals || {}
            toast.success(`Merged ${t.assets_removed || 0} duplicate assets into ${t.groups || 0}`)
            reset()
            onSuccess?.()
            onClose()
          }
        } else if (state === 'FAILURE') {
          stopPolling()
          setPhase(null)
          setProgress(null)
          toast.error(error || 'Dedupe failed. Please try again.')
        }
      } catch {
        stopPolling()
        setPhase(null)
        setProgress(null)
        toast.error('Lost connection to the dedupe task.')
      }
    }, POLL_INTERVAL)
  }

  const runDedupe = async (dryRun) => {
    setPhase(dryRun ? 'analyzing' : 'merging')
    setProgress(null)
    try {
      const data = new FormData()
      data.append('dry_run', dryRun ? 'true' : 'false')
      const res = await api.post('/assets/assets/dedupe/', data)
      pollStatus(res.data.task_id, dryRun)
    } catch (error) {
      setPhase(null)
      const msg = error.response?.data?.error || 'Could not start the dedupe task.'
      toast.error(msg)
    }
  }

  const totals = preview?.totals
  const groups = preview?.groups || []
  const pct = progress?.total ? Math.min(100, Math.round((progress.processed / progress.total) * 100)) : null
  const noDuplicates = preview && totals?.groups === 0

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Merge Duplicate Assets"
      maxWidth="md"
      disableBackdropClick={busy}
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<Analytics />}
            onClick={() => runDedupe(true)}
            loading={phase === 'analyzing'}
            disabled={phase === 'merging'}
          >
            {preview ? 'Re-analyze' : 'Analyze'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<MergeType />}
            onClick={() => runDedupe(false)}
            loading={phase === 'merging'}
            disabled={!preview || phase === 'analyzing' || noDuplicates}
          >
            {preview ? `Merge ${totals?.assets_removed || 0} Duplicates` : 'Merge Duplicates'}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Alert severity="info">
          Analyze scans every asset and groups those that are identical in content —
          the auto-generated tag and database id are ignored. Each group is then merged
          into its oldest record, the units are added together, and any work orders,
          fault reports, contracts and other links are moved onto the surviving asset.
        </Alert>

        <Alert severity="warning">
          Merging permanently deletes the duplicate records (after moving their links).
          Always Analyze first and review the preview before merging.
        </Alert>

        {busy && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {phase === 'analyzing' ? 'Scanning for duplicates…' : 'Merging duplicates…'}
              {progress?.total ? ` ${progress.processed} / ${progress.total} groups` : ''}
            </Typography>
            <LinearProgress variant={pct === null ? 'indeterminate' : 'determinate'} value={pct ?? 0} />
          </Box>
        )}

        {preview && !busy && (
          <>
            <Divider />
            {noDuplicates ? (
              <Alert severity="success">No duplicate assets found. Nothing to merge.</Alert>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Preview {preview.dry_run ? '(nothing merged yet)' : ''}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip color="primary" label={`${totals.groups} duplicate groups`} />
                    <Chip color="warning" label={`${totals.assets_removed} assets to remove`} />
                    <Chip color="success" label={`${totals.units_after} units after merge`} />
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Duplicate groups
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Asset</TableCell>
                        <TableCell>Facility</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Room</TableCell>
                        <TableCell align="right">Copies</TableCell>
                        <TableCell align="right">Units after</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groups.map((g) => (
                        <TableRow key={g.survivor_id}>
                          <TableCell>{g.name}</TableCell>
                          <TableCell>{g.facility || '—'}</TableCell>
                          <TableCell>{g.location || '—'}</TableCell>
                          <TableCell>{g.room || '—'}</TableCell>
                          <TableCell align="right">{g.count}</TableCell>
                          <TableCell align="right">{g.unit_total}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {totals.groups > groups.length && (
                    <Typography variant="caption" color="text.secondary">
                      Showing {groups.length} of {totals.groups} groups.
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Stack>
    </Modal>
  )
}

export default AssetDedupeDialog
